// Copyright (c) 2019, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import { FixedSizeList as List } from 'react-window'
import moment from 'moment'

import {
  Spinner,
  SvgIcon,
  Warning
} from '../'

import {
  default as EventListItemRenderer
} from './EventListItemRenderer'

const eventsListRef = React.createRef()

const QUERY_MESSAGE_EVENTS = gql`
  query($messageId: String!, $id: ID!) {
    events(sourceModel: "whatsappmessage", sourceNodeId: $messageId, orderBy: "created_at") {
      edges {
        node {
          id
          eventAt
          eventType
          eventData
          sourceNodeId
        }
      }
    }
    whatsappmessage(id: $id) {
      id
      createdAt
      createdBy {
        id
        firstName
        lastName
      }
      scheduledAt
      updatedAt
      updatedBy {
        id
        firstName
        lastName
      }
    }
  }
`

const WhatsAppMessageEventsList = ({ messageId }) => (
  <Query
    query={QUERY_MESSAGE_EVENTS}
    variables={{ messageId, id: messageId }}>
    {({ loading, error, data }) => {
      if (loading) {
        return (
          <div className='slds-is-relative' style={{ height: '80vh' }}>
            <Spinner type='brand' size='large' />
          </div>
        )
      }

      if (error) {
        return (
          <Warning>
            Failed to fetch message events.
          </Warning>
        )
      }

      if (data.events.edges.length === 0) {
        return (
          <Warning>
            No corresponding message events found.
          </Warning>
        )
      }

      let messageIndexInEventResults = data.events.edges.findIndex(({ node: event }) => (
        event.sourceNodeId === messageId
      ))
      let { createdAt, createdBy, scheduledAt, updatedAt, updatedBy } = data.whatsappmessage

      return (
        <div className='slds-grid slds-grid_vertical slds-size_1-of-1'>
          <div className='slds-col slds-m-bottom_x-small rounded-container'>
            <div className='u-font-size-large slds-p-top_small slds-p-bottom_small slds-border_bottom slds-m-bottom_x-small slds-align_absolute-center'>
              Actions
            </div>
          </div>
          <div className='slds-col slds-m-bottom_x-small rounded-container'>
            <div className='u-font-size-large slds-p-top_small slds-p-bottom_small slds-border_bottom slds-align_absolute-center'>
              Meta info
            </div>
            <div className='slds-text-align_left section-details'>
              <div className='slds-m-left_x-small slds-grid'>
                <SvgIcon
                  variant='utility'
                  symbol='clock'
                  size='small'
                  colour='light'
                  className='slds-m-right_small'
                />
                {`Scheduled at ${scheduledAt ? scheduledAt : '-'}`}
              </div>
              <div className='slds-m-left_x-small slds-m-top_x-small slds-grid'>
                <SvgIcon
                  variant='utility'
                  symbol='add'
                  size='small'
                  colour='light'
                  className='slds-m-right_small'
                />
                <div>
                  {`Created at ${moment(createdAt).format('DD MMM YYYY HH:mm:ss')}`}
                  <br />
                  &ensp;
                  {`by ${createdBy ? `${createdBy.firstName} ${createdBy.lastName}` : 'Unknown'}`}
                </div>
              </div>
              <div className='slds-m-left_x-small slds-m-top_x-small slds-grid'>
                <SvgIcon
                  variant='utility'
                  symbol='edit'
                  size='small'
                  colour='light'
                  className='slds-m-right_small'
                />
                <div>
                  {`Updated at ${moment(updatedAt).format('DD MMM YYYY HH:mm:ss')}`}
                  <br />
                  &ensp;
                  {`by ${updatedBy ? `${updatedBy.firstName} ${updatedBy.lastName}` : 'Unknown'}`}
                </div>
              </div>
            </div>
          </div>
          <div className='slds-col rounded-container'>
            <div className='u-font-size-large slds-p-top_small slds-p-bottom_small slds-border_bottom slds-align_absolute-center'>
              Events
            </div>
            <div className='timeline section-details'>
              <List
                ref={eventsListRef}
                itemData={data.events.edges}
                height={350}
                itemCount={data.events.edges.length}
                itemSize={50}
                width='100%'
                initialScrollOffset={messageIndexInEventResults}>
                {EventListItemRenderer}
              </List>
            </div>
          </div>
        </div>
      )
    }}
  </Query>
)

WhatsAppMessageEventsList.propTypes = {
  messageId: PropTypes.string.isRequired
}

export default WhatsAppMessageEventsList



// WEBPACK FOOTER //
// ./src/components/whatsapp-message-events-list/index.js